'use client';

import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useLocale } from 'next-intl';
import { useRef } from 'react';

import DrupalImage from '@commons/components/Image/Drupal';
import Text from '@commons/components/Text';
import VideoPlayer from '@commons/components/VideoPlayer';
import BaseContainer from '@commons/containers/BaseContainer';
import Flex from '@commons/containers/Flex';
import { usePageContext } from '@commons/contexts/page';
import { MediaQuery, breakpoints, useMediaQueries } from '@commons/contexts/responsive';
import { generateId } from '@commons/helpers/string';
import { asAbsoluteURL } from '@commons/helpers/url';
import { LOCALES } from '@commons/i18n';
import {
  DRUPAL_MEDIA_BUNDLE,
  type DrupalImage as DrupalImageType,
  type DrupalVideo,
} from '@commons/types/drupal';

import type { ScrollableHeadlineProps } from '.';
import styles from './ScrollableHeadline.module.scss';

// GSAP Plugins registration.

gsap.registerPlugin(ScrollTrigger);

/**
 * Scrollable Headline block component.
 *
 * @see https://gsap.com/resources/React/
 * @see https://gsap.com/docs/v3/Plugins/ScrollTrigger/
 */

const ScrollableHeadlineBlock = ({ uuid, content }: ScrollableHeadlineProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const currentLocale = useLocale();
  const { isEditing } = usePageContext();
  const { ready, minWidth } = useMediaQueries();

  const isEN = currentLocale == LOCALES.EN;
  const isMobile = ready && !minWidth.md;

  const id = generateId(`pin-${uuid}`);

  const { headlines = [], media, scrollableText, theme = 'grey-11' } = content;

  /**
   * This will pin the block when it reaches the top of the viewport.
   * On scroll, it will slide the scrollable text upwards.
   */
  useGSAP(
    () => {
      if (isEditing) return;

      const animatedText = document.querySelector('.animated-text') as HTMLElement;

      if (animatedText) {
        // Initial setup for the text element to be below the container.
        const textHeight = animatedText.clientHeight + (!isMobile ? 32 : 0);

        gsap.to(animatedText, {
          y: `-${textHeight}`,
          ease: 'none',
          scrollTrigger: {
            trigger: `#${id}`,
            pin: `#${id}`,
            scrub: true,
            start: 'top-=160 top',
            anticipatePin: 1,
            invalidateOnRefresh: true,
            end: `+=${textHeight * 2}`,
          },
        });

        // Slide-up animation for the text moving opposite to the scroll direction.
        gsap.to(animatedText, {
          y: 0,
          opacity: 1,
          ease: 'power2.out',
          scrollTrigger: {
            start: 'center-=34px center',
            end: 'top+=16 top',
            scrub: true,
          },
        });
      }
    },
    { dependencies: [isMobile, isEditing] }
  );

  return (
    <BaseContainer id={id} width={12} theme={theme} className={styles.wrapper} ref={containerRef}>
      <Text as="h2" level="display-1">
        {headlines[0]}
      </Text>
      <Flex gap="md" align="center">
        <Flex.Item width={{ xs: 5, md: 6, lg: 8 }} style={{ overflow: 'hidden' }}>
          <MediaQuery min="md">
            <ScrollableHeadlineMedia {...media} />
          </MediaQuery>
        </Flex.Item>
        <Flex.Item width={{ xs: 7, md: 6, lg: 4 }}>
          <Text as="h2" level="display-1">
            {headlines[1]}
          </Text>
        </Flex.Item>
      </Flex>
      <Text as="h2" level="display-1">
        {headlines[2]}
      </Text>
      <MediaQuery max="sm">
        <ScrollableHeadlineMedia {...media} />
      </MediaQuery>
      <BaseContainer
        width={{ xs: isEN ? 5 : 6, md: isEN ? 3 : 4 }}
        padding="xs"
        theme={theme}
        className={clsx(styles.pinWrap, 'animated-text')}
      >
        <Text level={{ xs: 'h4', lg: 'h2' }} color="aqua-2" marginBottom="none">
          {scrollableText}
        </Text>
      </BaseContainer>
    </BaseContainer>
  );
};

/**
 * ScrollableHeadline Media component.
 *
 * Renders an image or video based on the media type.
 */

const ScrollableHeadlineMedia = (media: DrupalImageType | DrupalVideo) => {
  const { bundle, url } = media;

  const mediaURL = asAbsoluteURL(url);

  if (bundle == DRUPAL_MEDIA_BUNDLE.Video) {
    return (
      <VideoPlayer
        src={mediaURL}
        loop
        muted
        playing
        playsInline
        displayLoading={false}
        className={styles.mediaWrapper}
      />
    );
  }

  return (
    <DrupalImage
      {...media}
      sizes={`(max-width: ${breakpoints.md}px) 100vw, (max-width: ${breakpoints.xl}px) 50vw, 1200px`}
      className={styles.mediaWrapper}
    />
  );
};

export default ScrollableHeadlineBlock;
